import { LocalizationString } from '@celito.clients/assets';
import { FormEngineModeTypeEnum, ObjectEnum } from '@celito.clients/enums';
import { FormEngine } from '@celito.clients/form-engine';
import { useActiveModule } from '@celito.clients/hooks';
import { errorToast } from '@celito.clients/utils';
import { SelectionEvents } from '@fluentui/react-combobox';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Tooltip,
} from '@fluentui/react-components';
import { AxiosError } from 'axios';
import cn from 'classnames';
import { useFormEngineContext } from 'libs/form-engine/src/lib/hooks';
import { useEffect, useRef } from 'react';

import { RowItem } from '../../grid-view-new/src/types';
import Icon from '../../icon/icon.component';
import { IconButton } from '../../icon-button/icon-button.component';
import { IOption } from '../../in-house-input-select/in-house-input-select.model';
import { ICommonData } from '../../in-house-input-select/services';
import { InHouseInputSelect } from '../../shared';
import ReferenceSelectorGrid from './components/reference-selector-grid';
import { ShowAllResultPanel } from './components/show-all-results-panel/show-all-results-panel.component';
import {
  ReferenceSelectorItem,
  ReferenceSelectorProps,
} from './reference-selector.model';
import classes from './reference-selector.module.css';
import { getObjects } from './services/getSelectorData';
import { getUserFilter } from './utils/filters-by-object';

interface ReferenceSelectorViewProps extends ReferenceSelectorProps {
  onRemoveItem: (item: RowItem) => void;
  onDownloadItem?: (item: RowItem) => void;
  showDownloadButton: boolean | undefined;
  onOptionSelect: (
    _ev: SelectionEvents,
    optionText: string,
    optionValue: string,
    selectedItems: IOption[] | IOption,
    data?: ICommonData
  ) => void;
  searchKey: string;
  setSearchKey: (searchKey: string) => void;
  isShowAllResultsViewOpen: boolean;
  onShowAllResult: () => void;
  handleOnAddClick: (items: ReferenceSelectorItem[]) => void;
  // Inline creation props
  isInlineCreationDialogOpen: boolean;
  handleInlineCreationOpen: () => void;
  handleInlineCreationClose: () => void;
  handleRecordCreated: (newRecord: ReferenceSelectorItem) => void;
  hasCreatePermission: boolean;
}

const ReferenceSelectorView: React.FC<ReferenceSelectorViewProps> = ({
  selectedItems,
  onRemoveItem,
  onDownloadItem,
  label,
  referencePickerProps,
  objectName,
  onRowReorder,
  canReorderRows,
  pickerClassName = '',
  showDownloadButton = false,
  required = false,
  mode,
  searchKey,
  setSearchKey,
  isShowAllResultsViewOpen,
  onOptionSelect,
  onShowAllResult,
  handleOnAddClick,
  onSelectorChange,
  getLatestVersionOnly,
  filterByExactRoles = false,
  excludeSelfAttachedId,
  setIsLoading,
  isLoading,
  getAllVersions,
  lookupColumnNames,
  // Inline creation props
  allowInlineRecordCreation,
  inlineCreationViewName,
  inlineCreationButtonTitle,
  isInlineCreationDialogOpen,
  handleInlineCreationOpen,
  handleInlineCreationClose,
  handleRecordCreated,
  hasCreatePermission,
}): JSX.Element => {
  const hasUpdatedItemsWithDepartment = useRef(false);
  const { objectDefinition } = useFormEngineContext();

  useEffect(() => {
    if (
      mode &&
      mode === FormEngineModeTypeEnum.Edit &&
      objectName === ObjectEnum.USER &&
      selectedItems?.length > 0 &&
      !hasUpdatedItemsWithDepartment.current
    ) {
      const items = selectedItems as unknown as ReferenceSelectorItem[];

      setIsLoading?.(true);
      const userNameList = items.map((item) => item.name);
      const params = {
        limit: userNameList.length,
        page: 1,
      };

      getObjects(
        objectName,
        objectDefinition?.moduleName ? [objectDefinition?.moduleName] : [],
        params,
        getUserFilter(userNameList),
        undefined,
        undefined,
        filterByExactRoles
      )
        .then((res) => {
          const objects = res.objects as ReferenceSelectorItem[];

          onSelectorChange(objects, false);

          setIsLoading?.(false);
          hasUpdatedItemsWithDepartment.current = true;
        })
        .catch((error) => {
          if ((error as AxiosError)?.name !== 'TypeError') {
            errorToast({
              message: `Could not fetch the items`,
            });
          }
        });
    }
  }, [selectedItems]);

  return (
    <div className={classes.selectorContainer}>
      <div className={classes.pickerWithButtonContainer}>
        <div className={cn(classes.pickerContainer, pickerClassName)}>
          <InHouseInputSelect
            onOptionSelect={(_, data) =>
              onOptionSelect(
                _,
                data.optionText,
                data.optionValue,
                data.selectedOptions,
                data.data
              )
            }
            label={label}
            required={referencePickerProps.required || required}
            options={[]}
            referencePicker={{
              objectName,
              onIconClick: onShowAllResult,
              pickerType: referencePickerProps.referencePicker?.pickerType,
              getLatestVersionOnly: getLatestVersionOnly,
              getAllVersions: getAllVersions,
              ...referencePickerProps.referencePicker,
            }}
            selectedOptions={selectedItems?.map((selected) => {
              return {
                text: selected.label,
                value: selected.name,
                version: selected.version ? selected.version : '',
              };
            })}
            dontShowOptions
            errorMessage={referencePickerProps.errorMessage}
            disabled={referencePickerProps.disabled}
            completeAttributeConfig={
              referencePickerProps.completeAttributeConfig
            }
          />
        </div>

        {/* Inline Record Creation - Add New Icon (beside input) */}
        {allowInlineRecordCreation &&
          inlineCreationViewName &&
          !referencePickerProps.disabled &&
          hasCreatePermission && (
            <Tooltip
              content={inlineCreationButtonTitle ?? LocalizationString.ADD_NEW}
              relationship="description"
              positioning={{ position: 'above', align: 'center' }}
              withArrow
            >
              <button
                type="button"
                onClick={handleInlineCreationOpen}
                className={classes.inlineCreationIconButton}
                aria-label={
                  inlineCreationButtonTitle ?? LocalizationString.ADD_NEW
                }
              >
                <Icon iconName="AddCircle24Regular" />
              </button>
            </Tooltip>
          )}
      </div>

      {selectedItems?.length > 0 && (
        <ReferenceSelectorGrid
          items={selectedItems}
          onRemoveItem={onRemoveItem}
          onDownloadItem={onDownloadItem}
          objectName={objectName}
          onRowReorder={onRowReorder}
          canReorderRows={canReorderRows}
          showDownloadButton={showDownloadButton}
          loading={isLoading}
          disabled={referencePickerProps.disabled}
          label={label}
          referenceUrl={referencePickerProps.referencePicker?.referenceUrl}
          lookupColumnNames={lookupColumnNames}
        />
      )}

      {isShowAllResultsViewOpen && (
        <ShowAllResultPanel
          excludeItems={selectedItems}
          objectName={objectName}
          searchKey={searchKey}
          setSearchKey={setSearchKey}
          isOpen={isShowAllResultsViewOpen}
          onDismiss={onShowAllResult}
          onAddClick={handleOnAddClick}
          label={label}
          defaultReferenceFilter={
            referencePickerProps?.referencePicker?.defaultReferenceFilter
          }
          multiSelect={referencePickerProps.multiselect}
          excludeSelfAttachedId={excludeSelfAttachedId}
          referenceUrl={referencePickerProps.referencePicker?.referenceUrl}
          lookupColumnNames={lookupColumnNames}
        />
      )}

      {/* Inline Record Creation Panel */}
      {allowInlineRecordCreation && inlineCreationViewName && (
        <Drawer
          type="overlay"
          open={isInlineCreationDialogOpen}
          position="end"
          className={classes.inlineCreationDrawer}
        >
          <DrawerHeader style={{ padding: 0 }}>
            <DrawerHeaderTitle
              className={classes.inlineCreationDialogTitle}
              action={
                <IconButton
                  appearance="subtle"
                  icon={<Icon iconName="Dismiss24Regular" />}
                  onClick={handleInlineCreationClose}
                />
              }
            >
              {inlineCreationButtonTitle ?? LocalizationString.ADD_NEW}
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody style={{ padding: 0 }}>
            <FormEngine
              viewName={inlineCreationViewName}
              mode={FormEngineModeTypeEnum.Create}
              onCancel={handleInlineCreationClose}
              onSubmit={(recordData) => {
                if (recordData && recordData.name) {
                  // Auto-add the newly created record to the selection
                  const newRecord: ReferenceSelectorItem = {
                    ...recordData,
                    key: String(recordData.name),
                    label: String(recordData.label ?? recordData.name),
                    name: String(recordData.name),
                  };
                  handleRecordCreated(newRecord);
                } else {
                  handleInlineCreationClose();
                }
              }}
              onSave={(recordData) => {
                if (recordData && recordData.name) {
                  // Auto-add the saved record to the selection (same as Submit)
                  const newRecord: ReferenceSelectorItem = {
                    ...recordData,
                    key: String(recordData.name),
                    label: String(recordData.label ?? recordData.name),
                    name: String(recordData.name),
                  };
                  handleRecordCreated(newRecord);
                } else {
                  handleInlineCreationClose();
                }
              }}
            />
          </DrawerBody>
        </Drawer>
      )}
    </div>
  );
};

export default ReferenceSelectorView;
